import { Wind } from 'lucide-react';
import type { CurrentConditions } from '../types';

export default function WindCompass({ current }: { current: CurrentConditions | null }) {
  if (!current || current.windMph == null || current.windDirDeg == null) return null;

  const { windMph, gustMph, windDir, windDirDeg } = current;
  const gustMeaningful = gustMph != null && gustMph > windMph;

  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider opacity-70 mb-2 flex items-center gap-2">
        <Wind className="w-3.5 h-3.5" /> Wind direction
      </h2>
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 flex items-center gap-4">
        <WindBarbCompass dirDeg={windDirDeg} mph={windMph} size={120} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-light tabular-nums">{windMph}</span>
            <span className="text-sm opacity-70">mph</span>
          </div>
          <div className="text-sm opacity-90">
            from {windDir ?? degLabel(windDirDeg)}
            <span className="opacity-50 text-xs ml-1">· {Math.round(windDirDeg)}°</span>
          </div>
          {gustMeaningful && (
            <div className="text-xs opacity-75">gusting to {gustMph} mph</div>
          )}
          <div className="text-[10px] opacity-50 mt-1">{sourceLabel(current)}</div>
          <div className="text-[10px] opacity-50 mt-0.5">
            ▬▬ = 10 mph · ▬ = 5 mph · ◣ = 50 mph
          </div>
        </div>
      </div>
    </section>
  );
}

// Aviation-style wind-barb compass.
//
// The staff crosses through center. On the "from" end (where wind comes
// from, determined by windDirDeg) are the speed feathers: a short tick is
// 5 mph, a long tick is 10 mph, a solid triangle pennant is 50 mph. On the
// opposite ("to") end is an arrow head pointing in the direction the wind
// is going. This keeps both direction pieces visible at once: the feathers
// tell you where it's coming from at a glance, the arrow where it's headed.
//
// Rotation: windDirDeg = direction wind is coming FROM (0 = from north).
// Base shape is drawn "from-is-up" (feathers at top, arrow at bottom), then
// the whole assembly rotates by windDirDeg.
function WindBarbCompass({
  dirDeg,
  mph,
  size = 96,
}: {
  dirDeg: number;
  mph: number;
  size?: number;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;

  const ticks: Array<{ deg: number; cardinal: boolean }> = [];
  for (let i = 0; i < 16; i++) {
    ticks.push({ deg: i * 22.5, cardinal: i % 4 === 0 });
  }

  // Barb accounting in mph (aviation is knots; we adapt for our units).
  const rounded = Math.max(0, Math.round(mph / 5) * 5);
  let remaining = rounded;
  const pennants = Math.floor(remaining / 50);
  remaining -= pennants * 50;
  const fullBarbs = Math.floor(remaining / 10);
  remaining -= fullBarbs * 10;
  const halfBarbs = Math.floor(remaining / 5);

  // Staff geometry (in the "from-is-up" base frame, origin at center of
  // viewBox). The FROM tip is at y = -r; the TO tip is at y = +(r - 2).
  const fromTipY = -r;
  const toTipY = r - 2;
  // Bright amber — high contrast against the app's variable gradient
  // backgrounds (sky blue → indigo → slate → amber-yellow). Amber reads
  // clearly on all of them and matches aviation-chart conventions.
  const staffColor = 'rgb(252, 211, 77)';
  const featherColor = 'rgb(252, 211, 77)';

  const barbLen = r * 0.42;
  const barbSpacing = 6; // px along staff between adjacent barbs
  const barbAngleDeg = 60; // barbs tilt 60° from the staff

  // Barb drawing: start at the FROM tip, work inward (toward center).
  // Offset the first barb slightly inward so it doesn't overrun the tip.
  let y = fromTipY + 1;

  const barbEls: React.ReactNode[] = [];
  let key = 0;

  for (let i = 0; i < pennants; i++) {
    // Triangle pennant: base on staff, tip outward to the left at 60°
    const angleRad = (barbAngleDeg * Math.PI) / 180;
    const baseLen = 5; // along-staff length of the pennant's base
    const tipX = -barbLen * Math.sin(angleRad);
    const tipY = y + barbLen * Math.cos(angleRad);
    const base1Y = y;
    const base2Y = y + baseLen;
    barbEls.push(
      <path
        key={key++}
        d={`M 0 ${base1Y} L ${tipX} ${tipY} L 0 ${base2Y} Z`}
        fill={featherColor}
      />,
    );
    y += baseLen + 2;
  }
  for (let i = 0; i < fullBarbs; i++) {
    const angleRad = (barbAngleDeg * Math.PI) / 180;
    const x2 = -barbLen * Math.sin(angleRad);
    const y2 = y + barbLen * Math.cos(angleRad);
    barbEls.push(
      <line
        key={key++}
        x1={0}
        y1={y}
        x2={x2}
        y2={y2}
        stroke={featherColor}
        strokeWidth={2}
        strokeLinecap="round"
      />,
    );
    y += barbSpacing;
  }
  for (let i = 0; i < halfBarbs; i++) {
    const angleRad = (barbAngleDeg * Math.PI) / 180;
    const halfLen = barbLen / 2;
    const x2 = -halfLen * Math.sin(angleRad);
    const y2 = y + halfLen * Math.cos(angleRad);
    // Half-barbs conventionally aren't placed at the very tip when there
    // are no full barbs/pennants — nudge in a bit if this is the only kind.
    if (fullBarbs === 0 && pennants === 0 && i === 0) y += 3;
    barbEls.push(
      <line
        key={key++}
        x1={0}
        y1={y}
        x2={x2}
        y2={y2}
        stroke={featherColor}
        strokeWidth={2}
        strokeLinecap="round"
      />,
    );
    y += barbSpacing;
  }

  // Calm indicator: if there are no barbs (speed < 5 mph), show a small
  // open circle at center per aviation convention for calm winds.
  const calm = pennants === 0 && fullBarbs === 0 && halfBarbs === 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
      aria-label={`Wind ${Math.round(mph)} mph from ${Math.round(dirDeg)} degrees`}
    >
      {/* Outer ring — darker + more opaque so everything on top pops */}
      <circle
        cx={cx}
        cy={cy}
        r={size / 2 - 2}
        fill="rgba(15, 23, 42, 0.8)"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="1.5"
      />

      {/* Tick marks */}
      {ticks.map((t) => {
        const rad = ((t.deg - 90) * Math.PI) / 180;
        const outer = size / 2 - 3;
        const inner = t.cardinal ? outer - 9 : outer - 4;
        const x1 = cx + Math.cos(rad) * inner;
        const y1 = cy + Math.sin(rad) * inner;
        const x2 = cx + Math.cos(rad) * outer;
        const y2 = cy + Math.sin(rad) * outer;
        return (
          <line
            key={t.deg}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={t.cardinal ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)'}
            strokeWidth={t.cardinal ? 1.5 : 0.7}
          />
        );
      })}

      {/* Cardinal labels — larger, bold, all four emphasized */}
      <text x={cx} y={14} textAnchor="middle" fill="rgba(255,255,255,1)" fontSize="13" fontWeight="800">N</text>
      <text x={size - 9} y={cy + 5} textAnchor="middle" fill="rgba(255,255,255,1)" fontSize="13" fontWeight="800">E</text>
      <text x={cx} y={size - 4} textAnchor="middle" fill="rgba(255,255,255,1)" fontSize="13" fontWeight="800">S</text>
      <text x={9} y={cy + 5} textAnchor="middle" fill="rgba(255,255,255,1)" fontSize="13" fontWeight="800">W</text>

      {calm ? (
        // Calm: double open circle at center (no staff, no barbs).
        <g>
          <circle cx={cx} cy={cy} r={6} fill="none" stroke={staffColor} strokeWidth={1.8} />
          <circle cx={cx} cy={cy} r={2.5} fill="none" stroke={staffColor} strokeWidth={1.5} />
        </g>
      ) : (
        // Barb: rotate the from-is-up base to the actual FROM direction.
        <g transform={`translate(${cx} ${cy}) rotate(${dirDeg})`}>
          {/* Staff */}
          <line
            x1={0}
            y1={fromTipY}
            x2={0}
            y2={toTipY}
            stroke={staffColor}
            strokeWidth={2.2}
            strokeLinecap="round"
          />
          {/* Arrow head at the TO end (bottom in base frame) */}
          <path
            d={`M 0 ${toTipY + 5} L -5 ${toTipY - 5} L 0 ${toTipY} L 5 ${toTipY - 5} Z`}
            fill={staffColor}
            stroke={staffColor}
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
          {/* Feathers at the FROM end (top in base frame) */}
          {barbEls}
        </g>
      )}

      {/* Center pin — only when not calm (calm has its own double circle) */}
      {!calm && <circle cx={cx} cy={cy} r={2.5} fill={staffColor} stroke="rgba(15,23,42,0.9)" strokeWidth="0.8" />}
    </svg>
  );
}

function degLabel(deg: number): string {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

function sourceLabel(current: CurrentConditions): string {
  if (current.source === 'pws') {
    return `From your PWS${current.stationName ? ` (${current.stationName})` : ''}`;
  }
  if (current.source === 'lora') {
    // stationName is "LoRa s2120-1" (or "LoRa station" with no device id) —
    // strip the leading "LoRa " so the parenthetical reads "(s2120-1)"; omit
    // it entirely if nothing meaningful is left.
    const deviceId = current.stationName?.replace(/^LoRa /, '').trim();
    const base = `From your LoRa station${deviceId && deviceId !== 'station' ? ` (${deviceId})` : ''}`;
    if (current.stale) {
      return current.ageMinutes != null
        ? `${base} · no report for ${current.ageMinutes} min`
        : `${base} · no recent report`;
    }
    return base;
  }
  if (current.source === 'nws-obs') return 'NWS station observation';
  return '';
}
